import type { INumberResponse } from '@/openapi-clients-generated/talk';

export type MessagingNumber = Pick<INumberResponse, 'messagingStatus' | 'messagingCampaignStatus' | 'byocMessagingMode'>;

export interface MessagingDisplay {
    key:
        | 'awaitingRegistration'
        | 'awaitingActivation'
        | 'activating'
        | 'enabled'
        | 'archive'
        | 'byocDisabled'
        | 'byocReceiveOnly'
        | 'byocSendReceive';
    /** Font Awesome classes for the marker. */
    icon: string;
    /** The state itself, as one line. */
    tooltip: string;
    /** What to do about it, when there is anything to do — rendered on its own line where there's room. */
    action?: string;
    /** The tenant has to do something. Everything else is waiting on us or the carriers. */
    needsTenantAction?: boolean;
}

/** The state and its remedy as a single string, for somewhere with only one line to give (a tooltip). */
export function messagingSummary(display: MessagingDisplay): string {
    return display.action ? `${display.tooltip} ${display.action}` : display.tooltip;
}

/**
 * How a number's messaging state reads in a list, or `null` when messaging was never asked for.
 *
 * BYOC comes first and is answered on its own terms: `byocMessagingMode` is non-null only for a
 * bring-your-own-carrier DID, whose messaging is an immediate per-number policy rather than a 10DLC
 * campaign. `messagingStatus` stays null on those numbers, so the native branch below would report
 * "never requested" for one that is actively sending.
 *
 * For native numbers, `messagingStatus` alone can't say what "pending" is waiting on: a number is
 * pending from the moment messaging is requested, whether the tenant has yet to file its 10DLC
 * registration or the carriers have yet to approve one already filed. `messagingCampaignStatus` is
 * what separates them — null means no campaign is bound, i.e. nothing has been submitted — and only
 * the first of those is something the tenant can act on.
 */
export function getMessagingDisplay(number: MessagingNumber): MessagingDisplay | null {
    switch (number.byocMessagingMode) {
        case 'disabled':
            return { key: 'byocDisabled', icon: 'fa-regular fa-comment-slash', tooltip: 'BYOC messaging is disabled' };
        case 'receiveOnly':
            return {
                key: 'byocReceiveOnly',
                icon: 'fa fa-comment-arrow-down',
                tooltip: 'BYOC messaging receives SMS/MMS; sending is disabled'
            };
        case 'sendReceive':
            return { key: 'byocSendReceive', icon: 'fa fa-comment-check', tooltip: 'BYOC messaging can send and receive SMS/MMS' };
    }

    switch (number.messagingStatus) {
        case 'pending':
            return number.messagingCampaignStatus
                ? {
                      key: 'awaitingActivation',
                      // Duotone: the two waiting-on-someone-else states read as quieter than the
                      // solid amber warning next to them, which is the one the tenant can act on.
                      icon: 'fad fa-hourglass-half',
                      tooltip: 'Text messaging requested — awaiting carrier approval of your 10DLC registration.'
                  }
                : {
                      key: 'awaitingRegistration',
                      icon: 'fa fa-triangle-exclamation',
                      tooltip: 'Text messaging requested, but your business is not registered for 10DLC yet.',
                      action: 'Visit the ZynoTalk Overview page to fix it.',
                      needsTenantAction: true
                  };
        case 'activating':
            return {
                key: 'activating',
                icon: 'fad fa-arrows-rotate',
                tooltip: 'Registration approved — messaging is being switched on for this number.'
            };
        case 'enabled':
            return { key: 'enabled', icon: 'fa fa-check', tooltip: 'Messaging enabled' };
        case 'archive':
            return { key: 'archive', icon: 'fa-regular fa-box-archive', tooltip: 'Archive Only' };
        default:
            return null;
    }
}

/**
 * Whether the number can hold SMS conversations at all — it appears in the texting UI, carries an
 * unread count, and its history is readable.
 *
 * Native `archive` qualifies because the conversation history stays readable after sending stops;
 * BYOC `receiveOnly` qualifies because inbound is still accepted. Neither can send — see
 * `canSendSmsNumber`.
 *
 * The BYOC branch is entered on a mode being *present*, not on `!== null`: a payload that predates
 * the field, or a fixture that omits it, carries `undefined`, and treating that as BYOC would let
 * every number through regardless of its native status.
 */
export function canViewSmsNumber(number: MessagingNumber): boolean {
    if (number.byocMessagingMode) return number.byocMessagingMode !== 'disabled';
    return number.messagingStatus === 'enabled' || number.messagingStatus === 'archive';
}

/** Whether an outbound message can actually be dispatched from the number. */
export function canSendSmsNumber(number: MessagingNumber): boolean {
    if (number.byocMessagingMode) return number.byocMessagingMode === 'sendReceive';
    return number.messagingStatus === 'enabled';
}

/**
 * Whether text messaging is in play for the tenant at all.
 *
 * A null status means the number never asked for messaging, and `archive` means it only retains
 * history — neither is a number that will ever send, so neither is a reason to talk about 10DLC.
 * BYOC numbers are deliberately not counted: their messaging is a per-number policy that never
 * involves a 10DLC registration, which is the only thing this answer is used to prompt for.
 */
export function hasMessagingNumbers(numbers: MessagingNumber[]): boolean {
    return numbers.some(n => !!n.messagingStatus && n.messagingStatus !== 'archive');
}

/** Whether any number is stuck waiting on a 10DLC registration nobody has submitted yet. */
export function needsSmsRegistration(numbers: MessagingNumber[]): boolean {
    return numbers.some(n => n.messagingStatus === 'pending' && !n.messagingCampaignStatus);
}
